import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import {
  getSchoolProfiles,
  createSchoolProfile,
  updateSchoolProfile,
  deleteSchoolProfile,
  type SchoolProfileData,
} from "../lib/api";
import { useRealtime } from "../hooks/useRealtime";

interface SchoolProfileContextValue {
  profiles: SchoolProfileData[];
  activeProfile: SchoolProfileData | null;
  loading: boolean;
  error: string | null;
  realtimeStatus: string;
  isConnected: boolean;
  lastSyncTime: Date | null;
  refreshProfiles: () => Promise<void>;
  switchProfile: (profileId: string) => void;
  addProfile: (data: {
    name: string;
    institution?: string;
    city?: string;
    color?: string;
  }) => Promise<SchoolProfileData>;
  editProfile: (
    id: string,
    data: Partial<SchoolProfileData>,
  ) => Promise<SchoolProfileData>;
  removeProfile: (id: string) => Promise<void>;
}

const SchoolProfileContext = createContext<SchoolProfileContextValue | null>(
  null,
);

export function useSchoolProfile() {
  const context = useContext(SchoolProfileContext);
  if (!context) {
    throw new Error(
      "useSchoolProfile must be used within a SchoolProfileProvider",
    );
  }
  return context;
}

const ACTIVE_PROFILE_KEY = "activeSchoolProfileId";

interface SchoolProfileProviderProps {
  children: ReactNode;
}

export function SchoolProfileProvider({
  children,
}: SchoolProfileProviderProps) {
  const { token } = useAuth();
  const [profiles, setProfiles] = useState<SchoolProfileData[]>([]);
  const [activeProfile, setActiveProfile] = useState<SchoolProfileData | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshProfiles = useCallback(async () => {
    if (!token) {
      setProfiles([]);
      setActiveProfile(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getSchoolProfiles(token);
      setProfiles(data);

      // Restore active profile from localStorage or use default
      const savedProfileId = localStorage.getItem(ACTIVE_PROFILE_KEY);
      let found: SchoolProfileData | null =
        data.find((p) => p.id === savedProfileId) || null;

      if (!found) {
        found = data.find((p) => p.is_default) || data[0] || null;
      }

      setActiveProfile(found);
    } catch (e) {
      console.error("Error fetching school profiles:", e);
      setError("Erro ao carregar perfis de escola");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refreshProfiles();
  }, [refreshProfiles]);

  const switchProfile = useCallback(
    (profileId: string) => {
      const profile = profiles.find((p) => p.id === profileId);
      if (profile) {
        setActiveProfile(profile);
        localStorage.setItem(ACTIVE_PROFILE_KEY, profileId);
      }
    },
    [profiles],
  );

  const addProfile = useCallback(
    async (data: {
      name: string;
      institution?: string;
      city?: string;
      color?: string;
    }) => {
      if (!token) throw new Error("Not authenticated");
      const newProfile = await createSchoolProfile(data, token);
      setProfiles((prev) => [...prev, newProfile]);

      // If this is the first profile, set it as active
      if (profiles.length === 0) {
        setActiveProfile(newProfile);
        localStorage.setItem(ACTIVE_PROFILE_KEY, newProfile.id);
      }

      return newProfile;
    },
    [token, profiles.length],
  );

  const editProfile = useCallback(
    async (id: string, data: Partial<SchoolProfileData>) => {
      if (!token) throw new Error("Not authenticated");
      const updated = await updateSchoolProfile(id, data, token);
      setProfiles((prev) => prev.map((p) => (p.id === id ? updated : p)));
      if (activeProfile?.id === id) {
        setActiveProfile(updated);
      }
      return updated;
    },
    [token, activeProfile?.id],
  );

  const removeProfile = useCallback(
    async (id: string) => {
      if (!token) throw new Error("Not authenticated");
      await deleteSchoolProfile(id, token);
      setProfiles((prev) => prev.filter((p) => p.id !== id));

      // If we deleted the active profile, switch to another one
      if (activeProfile?.id === id) {
        const remaining = profiles.filter((p) => p.id !== id);
        const newActive =
          remaining.find((p) => p.is_default) || remaining[0] || null;
        setActiveProfile(newActive);
        if (newActive) {
          localStorage.setItem(ACTIVE_PROFILE_KEY, newActive.id);
        } else {
          localStorage.removeItem(ACTIVE_PROFILE_KEY);
        }
      }
    },
    [token, activeProfile?.id, profiles],
  );

  const { status: realtimeStatus, isConnected, lastEventTime } = useRealtime(
    activeProfile?.id,
    token,
    undefined, // No custom callback needed, hook handles invalidation
    { debug: typeof process !== 'undefined' && process.env.NODE_ENV !== 'production' }
  );

  // Use lastEventTime from realtime hook directly
  const lastSyncTime = lastEventTime;

  const value: SchoolProfileContextValue = {
    profiles,
    activeProfile,
    loading,
    error,
    realtimeStatus,
    isConnected,
    lastSyncTime,
    refreshProfiles,
    switchProfile,
    addProfile,
    editProfile,
    removeProfile,
  };

  return (
    <SchoolProfileContext.Provider value={value}>
      {children}
    </SchoolProfileContext.Provider>
  );
}

export { SchoolProfileContext };
